import { PASSWORD_MIN_LENGTH, SETUP_TOKEN_PATTERN } from '@bartizo/shared';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { setupAdmin } from '../api/auth.js';
import { isApiError } from '../api/client.js';
import { useSession } from '../auth/session-context.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { TextField } from '../ui/Field.js';
import { ShieldIcon } from '../ui/icons.js';

/**
 * The first admin account, from the one-time link `install.sh central` printed
 * (knowledge doc section 9; P8.1).
 *
 * The token is the part of the link after `#`, which a browser never sends: it is
 * read here and posted once, so it reaches no request line and no server log. The
 * page asks for the address and the password twice, because there is nobody yet
 * who could reset a mistyped one. Spending the link signs the new admin in.
 */

const tokenFromLocation = (): string | null => {
  const token = window.location.hash.replace(/^#/, '');
  return SETUP_TOKEN_PATTERN.test(token) ? token : null;
};

export function SetupPage(): ReactNode {
  const { applySession } = useSession();
  const [token] = useState(tokenFromLocation);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    if (token === null) return;
    if (password !== repeat) {
      setError('The two passwords are not the same.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await setupAdmin(token, email, password);
      // The link is spent; it should not stay in the address bar or the history.
      window.history.replaceState(null, '', '/');
      applySession(session);
    } catch (caught: unknown) {
      setError(isApiError(caught) ? caught.message : 'Could not reach the Bartizo API.');
      setBusy(false);
    }
  };

  return (
    <main className="centred">
      <div className="card" style={{ width: 'min(26rem, 100%)' }}>
        <div className="card-body">
          <div className="stack" style={{ gap: 'var(--space-1)', marginBottom: 'var(--space-2)' }}>
            <ShieldIcon style={{ width: '2rem', height: '2rem', color: 'var(--accent)' }} />
            <h1>Set up Bartizo</h1>
            <p className="muted text-sm">
              The first account is an administrator. You can add others once you are in.
            </p>
          </div>

          {token === null ? (
            <Alert tone="warning" title="This page needs the setup link">
              Open the whole link the installer printed, including the part after #. If it is lost
              or older than a day, run <code>bartizo-api setup-link</code> on the central server for
              a new one.
            </Alert>
          ) : (
            <form onSubmit={(event) => void submit(event)} className="stack">
              <TextField
                label="Email"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
              <TextField
                label="Password"
                hint={`At least ${String(PASSWORD_MIN_LENGTH)} characters.`}
                type="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              <TextField
                label="Password again"
                type="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                value={repeat}
                onChange={(event) => {
                  setRepeat(event.target.value);
                }}
              />

              {error !== null && <Alert tone="danger">{error}</Alert>}

              <Button type="submit" variant="primary" block busy={busy} busyLabel="Setting up…">
                Create the administrator
              </Button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
